import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PushCategoriesView } from '@mdt/contracts';
import { Alert, Button, Checkbox, ErrorAlert, KeyedList, Loading, RadioGroup } from '../components/kit.tsx';
import { SafeLink } from '../components/SafeLink.tsx';
import { command, query } from '../lib/api.ts';
import { useFormat, usePageTitle } from '../lib/format.ts';
import { PushError, currentSubscription, disablePush, enablePush, permission, pushSupport } from '../lib/push.ts';
import { usePwa } from '../lib/pwa.tsx';
import { useSession, useSignedIn, type Calendar } from '../lib/session.tsx';
import { Sessions } from './Sessions.tsx';

export function Settings() {
  const { t } = useTranslation();
  usePageTitle(t('settings.title'));
  const pwa = usePwa();
  return (
    <div className="stack">
      <h1>{t('settings.title')}</h1>
      <LanguageAndCalendar />
      <Notifications />
      <Sessions />
      <section className="card" aria-labelledby="settings-access">
        <h2 id="settings-access">{t('activity.title')}</h2>
        <p>{t('activity.summary')}</p>
        <p><SafeLink href="/activity">{t('activity.open')}</SafeLink></p>
      </section>
      <section className="card" aria-labelledby="settings-install">
        <h2 id="settings-install">{t('install.title')}</h2>
        <p>{t('install.summary')}</p>
        <div className="row">
          {pwa.canInstall ? <Button onClick={() => void pwa.install()}>{t('install.button')}</Button> : null}
          <SafeLink href="/install">{t('install.link')}</SafeLink>
        </div>
      </section>
    </div>
  );
}

function LanguageAndCalendar() {
  const { t, i18n } = useTranslation();
  const { calendar, setCalendar, setLanguage } = useSession();
  const f = useFormat();
  return (
    <section className="card" aria-labelledby="settings-language">
      <h2 id="settings-language">{t('settings.languageTitle')}</h2>
      <RadioGroup legend={t('settings.language')} name="locale" value={i18n.language === 'th' ? 'th' : 'en'}
        options={[{ value: 'th', label: <span lang="th">ไทย</span> }, { value: 'en', label: <span lang="en">English</span> }]}
        onChange={(v) => setLanguage(v)} />
      <RadioGroup<Calendar> legend={t('settings.calendar')} name="calendar" value={calendar}
        options={[{ value: 'be', label: t('settings.calendarBe') }, { value: 'ce', label: t('settings.calendarCe') }]}
        onChange={setCalendar} />
      <p>{t('settings.preview')} <strong data-testid="date-preview">{f.dateLong(Date.now())}</strong></p>
      <p className="muted small">{t('settings.timeZone')}</p>
    </section>
  );
}

type PushConfig = { vapidPublicKey: string | null; allowed: boolean };
type Device = { subscriptionId: string; deviceLabel: string | null; createdAt: string; lastSuccessAt: string | null; thisDevice: boolean };
type Prefs = { locale: string | null; calendar: string | null; pushCategories: PushCategoriesView };

const CATEGORIES = ['matching', 'deadlines', 'schedule', 'grades', 'clearance'] as const;

/**
 * Notifications (roadmap 3.5): an explanation first, and the browser's permission prompt only after the
 * person presses "Enable notifications". Never offered on a shared computer.
 */
function Notifications() {
  const { t } = useTranslation();
  const { view } = useSignedIn();
  const qc = useQueryClient();
  const f = useFormat();
  const config = useQuery({ queryKey: ['push.config'], queryFn: () => query<PushConfig>('push.config') });
  const allowed = view.mode === 'standard' && !view.ghost && config.data?.allowed === true;
  const devices = useQuery({ queryKey: ['push.mine'], queryFn: () => query<Device[]>('push.mine'), enabled: allowed });
  const prefs = useQuery({ queryKey: ['prefs.mine'], queryFn: () => query<Prefs>('prefs.mine'), enabled: allowed });
  const here = useQuery({ queryKey: ['push.here'], queryFn: async () => (await currentSubscription()) !== null, enabled: allowed });
  const [notice, setNotice] = useState<string | null>(null);

  const refreshAll = () => Promise.all([qc.invalidateQueries({ queryKey: ['push.mine'] }), qc.invalidateQueries({ queryKey: ['push.here'] })]);
  const enable = useMutation({
    mutationFn: () => enablePush(config.data!.vapidPublicKey!),
    onSuccess: async () => {
      setNotice(t('push.enabled'));
      await refreshAll();
    },
  });
  const disable = useMutation({ mutationFn: disablePush, onSuccess: async () => {
    setNotice(t('push.disabled'));
    await refreshAll();
  } });
  const test = useMutation({ mutationFn: () => command('push.test', {}), onSuccess: () => setNotice(t('push.testSent')) });
  const remove = useMutation({ mutationFn: (id: string) => command('push.unsubscribe', { subscriptionId: id }), onSuccess: refreshAll });
  const category = useMutation({
    mutationFn: (patch: Partial<PushCategoriesView>) => command('prefs.update', { pushCategories: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prefs.mine'] }),
  });

  const support = pushSupport();
  let body;
  if (view.mode === 'shared') body = <p data-testid="push-shared">{t('push.sharedUnavailable')}</p>;
  else if (config.isPending) body = <Loading />;
  else if (!allowed || !config.data?.vapidPublicKey) body = <p>{t('push.notConfigured')}</p>;
  else if (support === 'install-first') body = <p>{t('push.installFirst')} <SafeLink href="/install">{t('install.link')}</SafeLink></p>;
  else if (support === 'unsupported') body = <p>{t('push.unsupported')}</p>;
  else {
    const denied = permission() === 'denied';
    body = (
      <>
        <p>{t('push.explain')}</p>
        <ul>
          <li>{t('push.explainNoPersonal')}</li>
          <li>{t('push.explainDevice')}</li>
        </ul>
        {denied ? <Alert tone="warning"><p>{t('push.blocked')}</p></Alert> : null}
        {enable.error ? (enable.error instanceof PushError
          ? <Alert tone="error"><p>{t(enable.error.reason === 'denied' ? 'push.blocked' : 'push.failed')}</p></Alert>
          : <ErrorAlert error={enable.error} />) : null}
        <ErrorAlert error={disable.error ?? test.error ?? remove.error ?? category.error} />
        {notice ? <Alert tone="success"><p>{notice}</p></Alert> : null}
        <div className="row">
          {here.data
            ? (
              <>
                <Button variant="secondary" busy={disable.isPending} onClick={() => disable.mutate()}>{t('push.disable')}</Button>
                <Button variant="secondary" busy={test.isPending} onClick={() => test.mutate()}>{t('push.test')}</Button>
              </>
            )
            : <Button busy={enable.isPending} disabled={denied} onClick={() => enable.mutate()}>{t('push.enable')}</Button>}
        </div>
        {prefs.data ? (
          <fieldset>
            <legend>{t('push.categories')}</legend>
            {CATEGORIES.map((c) => (
              <Checkbox key={c} label={t(`push.category.${c}`)} checked={prefs.data.pushCategories[c]}
                disabled={category.isPending} onChange={(v) => category.mutate({ [c]: v })} />
            ))}
          </fieldset>
        ) : null}
        {devices.data?.length ? (
          <>
            <h3>{t('push.devices')}</h3>
            <KeyedList items={devices.data} getKey={(d) => d.subscriptionId}>
              {(d) => (
                <div className="row">
                  <span>
                    {d.deviceLabel ?? t('push.unknownDevice')} {d.thisDevice ? <span className="tag">{t('push.thisDevice')}</span> : null}
                    <br />
                    <span className="muted small">{t('push.since', { when: f.dateTime(d.createdAt) })}</span>
                  </span>
                  {!d.thisDevice ? (
                    <Button variant="secondary" busy={remove.isPending && remove.variables === d.subscriptionId}
                      onClick={() => remove.mutate(d.subscriptionId)}>{t('push.remove')}</Button>
                  ) : null}
                </div>
              )}
            </KeyedList>
          </>
        ) : null}
      </>
    );
  }

  return (
    <section className="card" aria-labelledby="settings-push">
      <h2 id="settings-push">{t('push.title')}</h2>
      {body}
    </section>
  );
}
